import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { forgotPassword } from '../services/api';
import { WhatsAppHelpText } from '../components/WhatsAppButton';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="JustDeliver" className="h-12 w-auto" />
        </div>

        {sent ? (
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
              </div>
              <h2 className="text-xl font-bold font-['Inter'] text-gray-900 mb-3">Check your email</h2>
              <p className="text-gray-600 mb-2">If an account exists for:</p>
              <p className="font-semibold text-gray-900 mb-4">{email}</p>
              <p className="text-sm text-gray-500 mb-6">We've sent a password reset link. The link expires in 1 hour.</p>
              <Link to="/login">
                <Button className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20]" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-200 shadow-none">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-semibold font-['Inter']">Forgot password?</CardTitle>
              <CardDescription className="text-gray-600">
                Enter your email and we'll send you a reset link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 rounded-sm"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm h-11"
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                  <ArrowLeft className="w-4 h-4" /> Back to Login
                </Link>
                <WhatsAppHelpText />
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
